import type { MenuItem } from "primereact/menuitem";
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import { useEffect, useMemo, useRef, useState } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { IconCalendar, IconDownload, IconFileTypeCsv, IconFileTypeXls, IconReload, IconSearch } from "@tabler/icons-react";
import { Button } from "primereact/button";
import Table from "./table";
import { FILTERS_STORAGE_KEY, FIND_TRIPS_PAGINATOR_KEY, FIND_TRIPS_TIME, HBL_PA_RESOURCES, RAILWAY_ZONES, REPORT_TYPES, REPORT_TYPES_DESCRIPTION, TAG_MISSING_NEW_TABLE } from "../constants/data.cnst";
import { Tag } from "primereact/tag";
import { ddmmyyyyToMmddyyyy, formatDateInDDMMYYYY, getSeverity, shortForm } from "../services/util.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { APIMapping } from "../constants/api-mapping.cnst";
import { fetchApi } from "../services/http.service";
import { Toast } from "primereact/toast";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "primereact/tooltip";
import { MultiSelect } from "primereact/multiselect";
import CardTable from "./card-table";
import BarChart from "./bar-charts";
import { Menu } from "primereact/menu";

interface ReportsProps {
    reportType: string;
    tableColumns: ColumnMeta[];
    tableData: any[];
}

type ReportTypes = typeof REPORT_TYPES;

type ReportSection = keyof ReportTypes["PARAMS"];

type ReportTypeKey<T extends ReportSection> = keyof ReportTypes["PARAMS"][T];

export default function Reports({
    reportType,
    tableColumns,
    tableData,
}: ReportsProps) {

    let description: string = '';
    let paramsReportType = "FIND_TRIPS";
    let reportSection: ReportSection | undefined = undefined;
    let breadcrumb: MenuItem[] = [];
    const location = useLocation();
    const navigate = useNavigate();
    localStorage.removeItem(FILTERS_STORAGE_KEY);


    if (window.location.pathname.includes('report')) {
        reportSection = window.location.pathname.split('/')[2].replaceAll('-', '_').toUpperCase() as ReportSection;
        paramsReportType = (REPORT_TYPES.PARAMS[reportSection][reportType as ReportTypeKey<ReportSection>] as string).replaceAll(' ', '_').toUpperCase();
        description = REPORT_TYPES_DESCRIPTION[reportSection][reportType as ReportTypeKey<ReportSection>] || "";
        reportType = REPORT_TYPES.HEADER[reportSection][reportType as ReportTypeKey<ReportSection>];

        breadcrumb = [
            { label: 'Operational Monitoring', style: { pointerEvents: 'none', fontWeight: 500 } },
            { label: reportSection?.toLowerCase()?.replace(/_/g, " ")?.replace(/\b\w/g, char => char.toUpperCase()) || '', style: { pointerEvents: 'none', fontWeight: 500 } },
            { label: reportType, style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
        ];


        if (breadcrumb[1].label?.startsWith('Station')) {
            if (reportType === REPORT_TYPES.HEADER.STATION_HEALTH.CHECKSUM_CHANGE_REPORT) {
                tableColumns = TABLE_COLUMNS_CONFIG.STATION_CHECKSUM_CHANGE_REPORT;
            }
            tableColumns = tableColumns
                .filter((column: ColumnMeta) => !column.header.includes('Trip'))
                .flatMap((column: ColumnMeta) => {
                    if (column.header.startsWith('Loco ID')) {
                        return [
                            {
                                header: 'Station ID',
                                field: 'stationId',
                                hasSort: true,
                                hasMultiColumnFilter: true
                            },
                            {
                                header: 'Station Name',
                                field: 'stationName',
                                hasSort: true,
                                hasMultiColumnFilter: true
                            },
                            {
                                header: 'Zone',
                                field: 'zone',
                                hasSort: true,
                                hasMultiColumnFilter: true
                            },
                            {
                                header: 'Project',
                                field: 'division',
                                hasSort: true,
                                hasMultiColumnFilter: true
                            },
                        ];
                    }

                    return [column];
                });
        }

    } else {
        breadcrumb = [
            { label: 'Find Trips', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
        ];
    }

    // const ALLOWED_REPORT_TYPES = [
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.READER_EFFICENCY,
    //     REPORT_TYPES.HEADER.EVENTS.UNWANTED_BRAKING,
    //     REPORT_TYPES.HEADER.EVENTS.TAG_MISSING,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.CAB_RFID_READER,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.RF_ISSUES_LOCO,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.BRAKE_VC_LOGS_MONITORING,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.SPEEDOMETER_HEALTH_MONITORING,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.CARD_STATUS,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.CARD_DIAGNOSTICS,
    //     REPORT_TYPES.HEADER.EVENTS.SIGNAL_BLANKING,
    //     REPORT_TYPES.HEADER.STATION_HEALTH.RF_ISSUES_STATION,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.DMI_HEALTH_MONITORING,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.CRASH_LOGS,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.RADIO_PARAMETERS_MONITORING,
    //     REPORT_TYPES.HEADER.LOCO_HEALTH.CHECKSUM_CHANGE_REPORT,
    //     REPORT_TYPES.HEADER.STATION_HEALTH.FIU,
    //     REPORT_TYPES.HEADER.STATION_HEALTH.CHECKSUM_CHANGE_REPORT,
    //     REPORT_TYPES.HEADER.STATION_HEALTH.SYSTEM_FAILURE,
    //     REPORT_TYPES.HEADER.STATION_HEALTH.EIGW_FAILURE,
    //     // 'Find Trips'

    // ];


    const shouldFetchReports = true;
    // ALLOWED_REPORT_TYPES.includes(
    //     reportType
    // );

    const savePaginatorToStorage = (filters: any) => {
        localStorage.setItem(FIND_TRIPS_PAGINATOR_KEY, JSON.stringify(filters));
    };

    const getPaginatorFromStorage = () => {
        try {
            return JSON.parse(localStorage.getItem(FIND_TRIPS_PAGINATOR_KEY) || "{}");
        } catch {
            return {};
        }
    };

    const clearPaginatorFromStorage = () => {
        localStorage.removeItem(FIND_TRIPS_PAGINATOR_KEY);
    };

    const tripFilterNotAllowedReportTypes = ![REPORT_TYPES.HEADER.LOCO_HEALTH.RF_ISSUES_LOCO, REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW].includes(reportType);

    const toast = useRef<Toast>(null);
    const [locoOrStationvalidationMsg, setLocoOrStationValidationMsg] = useState("");
    const [tripIdvalidationMsg, setTripIdValidationMsg] = useState("");

    const [displayTableData, setDisplayTableData] = useState<any[]>(tableData);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [pageNum, setPageNum] = useState(1);
    const [filtersChanged, setFiltersChanged] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const { today, pastMonth } = useMemo(() => {
        const t = new Date();
        const p = new Date();
        p.setMonth(t.getMonth() - 1);
        return { today: t, pastMonth: p };
    }, []);
    const [isQueryParamsProcessed, setIsQueryParamsProcessed] = useState(false);


    const [filters, setFilters] = useState({
        locoId: [] as string[],
        stationId: [] as string[],
        tripId: [] as string[],
        zone: [] as string[],
        date: [pastMonth, today] as (Date | null)[]
    });

    // useEffect(() => {
    //     handleReset();   // reuse your existing reset logic
    // }, [location.pathname, reportType]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const locoId = params.get("locoId")?.split(",") ?? [];
        const tripId = params.get("tripId")?.split(",") ?? [];
        const fromDate = params.get("fromDate");
        const toDate = params.get("toDate");
        const stationId = params.get("stationId")?.split(",") ?? [];

        if (locoId.length > 0 || fromDate || tripId.length > 0 || stationId.length > 0 || toDate) {
            setFilters(prev => ({
                ...prev,
                locoId: locoId ?? prev.locoId,
                stationId: stationId ?? prev.stationId,
                tripId: tripId ?? prev.tripId,
                date: fromDate
                    ? [
                        new Date(ddmmyyyyToMmddyyyy(fromDate)),
                        toDate ? new Date(ddmmyyyyToMmddyyyy(toDate)) : null
                    ]
                    : prev.date
            }));

            // clean URL after reading
            // navigate(location.pathname, { replace: true });
        } else {
            // 👉 only reset if NO query params
            handleReset(); // pass flag to avoid refetch
        }
        setIsQueryParamsProcessed(true);
    }, [location.search]);


    const handleReset = () => {
        clearPaginatorFromStorage()
        const resetFilters = {
            locoId: [],
            stationId: [],
            tripId: [],
            zone: [],
            date: [pastMonth, today]
        };
        setFiltersChanged(false);
        setFirst(0);
        setPageNum(1);
        setRows(10);
        setFilters(resetFilters);
        updateQueryParams(resetFilters);

        if (shouldFetchReports) {
            setTimeout(() => {
                refetch();
            }, 100)
        }
    };

    const updateQueryParams = (customFilters?: typeof filters) => {
        const f = customFilters ?? filters;
        const params = new URLSearchParams();

        if (f.locoId.length) params.set("locoId", f.locoId.join(","));
        if (f.stationId.length) params.set("stationId", f.stationId.join(","));
        if (f.tripId.length) params.set("tripId", f.tripId.join(","));

        if (f.date?.[0]) {
            params.set("fromDate", formatDateInDDMMYYYY(f.date[0]));
        }

        if (f.date?.[1]) {
            params.set("toDate", formatDateInDDMMYYYY(f.date[1]));
        }

        navigate(
            params.toString()
                ? `${location.pathname}?${params.toString()}`
                : location.pathname,
            { replace: true }
        );
    };

    const reportsQueryParams = useMemo<
        Record<string, string | number | boolean>
    >(() => {
        const params: Record<string, string | number | boolean> = {
            reportType: paramsReportType,
            page_num: pageNum,
            total_entry_per_page: rows
        };

        if (filters.locoId.length) params.locoId = filters.locoId.join(",");
        if (filters.stationId.length) params.stationId = filters.stationId.map(item => item.split(' (')[0]).join(",");
        if (filters.tripId.length) params.tripId = filters.tripId.join(",");
        if (filters.date?.[0]) params.fromDate = formatDateInDDMMYYYY(filters.date[0]);
        if (filters.date?.[1]) params.toDate = formatDateInDDMMYYYY(filters.date[1]);

        return params;
    }, [filters, reportType, pageNum, rows]);


    const { data: reportApiResponseData, isFetching: isReportDataLoading, refetch } = useQuery({
        queryKey: ["reports", JSON.stringify(reportsQueryParams)],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.REPORTS,
                queryParams: reportsQueryParams,
                resourceName: reportType === "Find Trips" ? "Trip Summary" :  "Reports_" + reportSection?.toLowerCase()?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) + "_" + reportType
            }),
        staleTime: 0,
        gcTime: 0,
        enabled: false || REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW === reportType
    });

    const isStationReport = reportSection?.includes("STATION");

    const { data: filtersResponseData } = useQuery({
        queryKey: ["loco-filters", { fromDate: formatDateInDDMMYYYY(filters.date[0]), toDate: formatDateInDDMMYYYY(filters.date[1]) }],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_FILTERS_FOR_EVENTS,
                queryParams: { ...{ fromDate: formatDateInDDMMYYYY(filters.date[0]), toDate: formatDateInDDMMYYYY(filters.date[1]) }, filterTypes: isStationReport ? 'STATION' : 'LOCO', dashboardCategory: paramsReportType },
                resourceName: HBL_PA_RESOURCES.REPORT
            }),
        staleTime: 0,
        gcTime: 0,
        enabled: isQueryParamsProcessed && !!filters.date[0]
    });

    const stationOrLocoOptions =
        (filtersResponseData?.response?.[
            isStationReport ? "stationIds" : "locoIds"
        ] ?? []).map((id: any) => ({
            label: id.toString(),
            value: id.toString(),
        }));

    const { data: filtersTripResponseData } = useQuery({
        queryKey: ["trip-filters", { fromDate: formatDateInDDMMYYYY(filters.date[0]), toDate: formatDateInDDMMYYYY(filters.date[1]) }],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_FILTERS_FOR_EVENTS,
                queryParams: { ...{ fromDate: formatDateInDDMMYYYY(filters.date[0]), toDate: formatDateInDDMMYYYY(filters.date[1]) }, filterTypes: 'TRIP', dashboardCategory: paramsReportType },
                resourceName: HBL_PA_RESOURCES.REPORT
            }),
        staleTime: 0,
        gcTime: 0,
        enabled: isQueryParamsProcessed && !!filters.date[0] && !isStationReport
    });

    const tripOptions =
        (filtersTripResponseData?.response?.tripIds ?? []).map((id: any) => ({
            label: id.toString(),
            value: id.toString(),
        }));

    useEffect(() => {
        if (!filtersResponseData) return;

        const filterKey = isStationReport ? "stationId" : "locoId";
        const responseKey = isStationReport ? "stationIds" : "locoIds";

        const validOptions = new Set(
            (filtersResponseData.response?.[responseKey] ?? []).map(String)
        );

        const selectedValues = filters[filterKey];

        const filteredSelection = selectedValues.filter((id: string | number) =>
            validOptions.has(String(id))
        );

        if (filteredSelection.length !== selectedValues.length) {
            setFilters(prev => ({
                ...prev,
                [filterKey]: filteredSelection
            }));

            setLocoOrStationValidationMsg(
                `Selected ${isStationReport ? "station(s)" : "loco(s)"} are no longer available for the selected date range. Please select again.`
            );
        } else {
            setLocoOrStationValidationMsg("");
        }
    }, [filtersResponseData]);

    useEffect(() => {
        if (!filtersTripResponseData) return;

        const validLocos = new Set(
            (filtersTripResponseData.response?.tripIds ?? []).map(String)
        );

        const filteredSelection = filters.tripId.filter(id =>
            validLocos.has(String(id))
        );

        if (filteredSelection.length !== filters.tripId.length) {
            setFilters(prev => ({
                ...prev,
                tripId: filteredSelection
            }));

            setTripIdValidationMsg(
                "Selected trip Id(s) are no longer available for the selected date range. Please select again."
            );
        } else {
            setTripIdValidationMsg("");
        }

    }, [filtersTripResponseData]);


    const filtersRef = useRef(filters);

    const {
        data: tagMissingCateogoryResponseData
    } = useQuery({
        queryKey: [
            "tag-missing-category",
            TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.api,
            filters.date[0],
            filters.date[1],
        ],

        queryFn: () =>
            fetchApi({
                endpoint: TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.api,
                queryParams: {
                    fromDate: formatDateInDDMMYYYY(filters.date[0]),
                    toDate: filters?.date[1] ? formatDateInDDMMYYYY(filters.date[1]) : formatDateInDDMMYYYY(filters.date[0])
                },
                resourceName: reportType === "Find Trips" ? "Trip Summary" : "Reports_" + reportSection?.toLowerCase()?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) + "_" + reportType
            }),

        staleTime: 0,
        gcTime: 0,
        enabled: Boolean(filters.date[0] || filters.date[0]) && reportType === "Tag Missing New",
    });

    const getTagMissingCategoryData = (data: any, key: string) => {
        return {
            labels: data?.map((item: any) => String(item[key])),


            datasets: TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.barChart.map((event) => ({
                label: event.label,
                backgroundColor: event.backgroundColor,
                data: data.map(
                    (item: any) =>
                        item?.category?.[event.key] ?? 0
                ),
                stationId: data?.map((item: any) => item?.stationId),
                stationName: data?.map((item: any) => item?.stationName),
            }))
        };
    };

    const tagMissedCategoryData = getTagMissingCategoryData(tagMissingCateogoryResponseData?.response ?? [], 'tagId');

    const cardHeader = (
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center">
                    <TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.icon size={22} className="text-pink-500"
                    />
                </div>

                <div>
                    <h3>
                        {TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.header}
                    </h3>
                    <p className="font-medium">
                        {TAG_MISSING_NEW_TABLE.TAG_MISS_CATEGORY.subtitle}
                    </p>
                </div>
            </div>

            {/* <Button
            label="Download"
            icon={<IconDownload size={20} />}
            loading={isDownloading}
            disabled={isDownloading || displayTableData.length === 0}
            className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500"
            onClick={() => shouldFetchReports && handleDownload()}
        /> */}
        </div>
    )

    //   const getLocoBarChartData = (data: any, key: string) => {
    //     return {
    //       labels: data.map((item: any) => String(item[key])),

    //       datasets: EVENT_CATEGORIES.filter(event => event.label !== 'Slip/Slide Detected').map((event) => ({
    //         label: event.label,
    //         backgroundColor: event.backgroundColor,
    //         data: data.map(
    //           (item: any) =>
    //             item?.breakdownByCategory?.[event.key] ?? 0
    //         )
    //       }))
    //     };
    //   };

    useEffect(() => {
        if (!isQueryParamsProcessed) return;
        if (shouldFetchReports) {
            refetch();
        }
    }, [pageNum, rows, isQueryParamsProcessed, location.pathname]);

    useEffect(() => {
        if (shouldFetchReports) {
            setDisplayTableData(reportApiResponseData?.response?.data ?? []);
            setTotalRecords(reportApiResponseData?.response?.total_entries ?? 0);
        } else {
            setDisplayTableData(tableData);
        }
    }, [reportApiResponseData, tableData, shouldFetchReports, filtersChanged]);

    useEffect(() => {
        filtersRef.current = filters;
        setFiltersChanged(true);
        // if (shouldFetchReports) {
        //     setDisplayTableData([]);
        //     setTotalRecords(0);
        // } else {
        //     setDisplayTableData(tableData);
        //     setTotalRecords(tableData.length);
        // }
        const paginator = reportType === 'Find Trips' ? getPaginatorFromStorage() : {};

        const page = paginator?.page ?? 1;
        const rows = paginator?.rows ?? 10;

        setPageNum(page);
        setRows(rows);
        setFirst((page - 1) * rows);
    }, [filters]);


    const exportQueryParams = useMemo(() => {
        if (!reportsQueryParams) return undefined;

        const { page_num, total_entry_per_page, ...rest } = reportsQueryParams;

        return rest;
    }, [reportsQueryParams]);


    // const { isFetching: isDownloading, refetch: downloader } = useQuery({
    //     queryKey: ["reports-download", JSON.stringify(exportQueryParams)],
    //     queryFn: () =>
    //         fetchApi({
    //             endpoint: APIMapping.REPORTS_EXPORT,
    //             responseType: "blob",
    //             queryParams: {...exportQueryParams, format: 'CSV' },
    //             resourceName: reportType === "Find Trips" ? "Trip Summary" : "Reports_" + reportSection?.toLowerCase()?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) + "_" + reportType
    //         }),
    //     staleTime: 0,
    //     gcTime: 0,
    //     enabled: false
    // });

    const { mutateAsync: downloadReport, isPending: isDownloading } = useMutation({
        mutationFn: (format: "CSV" | "EXCEL") =>
            fetchApi({
                endpoint: APIMapping.REPORTS_EXPORT,
                responseType: "blob",
                queryParams: {
                    ...exportQueryParams,
                    format,
                    _ts: Date.now(),
                },
                resourceName:
                    reportType === "Find Trips"
                        ? "Trip Summary"
                        :  "Reports_" + reportSection?.toLowerCase()?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) + "_" + reportType
            })
    });

    const handleDownload = async (format: "CSV" | "EXCEL") => {
        try {
            const result = await downloadReport(format);

            const blob = result.data as Blob;

            const contentDisposition = result.headers.get("content-disposition");
            let fileName = `${reportType}`;

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    fileName = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

            toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: `${reportType} report downloaded successfully`,
                life: 3000
            });
        } catch (err) {
            console.error("Download failed", err);

            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to download report. Please try again.",
                life: 4000
            });
        }
    };

    const items = [
        {
            label: "CSV",
            icon: <IconFileTypeCsv />,
            command: () => {
                shouldFetchReports && handleDownload("CSV");
            }
        },
        {
            label: "Excel",
            icon: <IconFileTypeXls />,
            command: () => {
                shouldFetchReports && handleDownload("EXCEL");
            }
        }
    ];
    const menu = useRef<Menu>(null);

    const header = (

        <>
            <div className="flex justify-between items-center">
                <h3 className="m-0 font-semibold">{reportType}</h3>

                <Button
                    label="Download"
                    icon={<IconDownload size={20} />}
                    loading={isDownloading}
                    disabled={isDownloading || displayTableData.length === 0}
                    className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
                    onClick={(event) => menu.current?.toggle(event)}
                />
                <Menu
                    popup
                    ref={menu}
                    model={items}
                    id="popup_menu"
                    popupAlignment="right"
                />
            </div>

            {REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW === reportType && (
                <div className="flex justify-end gap-2 mt-2">

                    {/* MultiSelect 1 */}
                    <MultiSelect
                        value={filters.zone}
                        options={RAILWAY_ZONES ?? []} // [{ label, value }]
                        optionLabel="zoneName"
                        filter
                        resetFilterOnHide
                        placeholder="Select Zones"
                        className="w-56"
                        display="chip"
                    />

                    <MultiSelect
                        value={filters.locoId}
                        options={stationOrLocoOptions ?? []} // [{ label, value }]
                        filter
                        resetFilterOnHide
                        placeholder="Select Locos"
                        className="w-56"
                        display="chip"
                    />

                    <MultiSelect
                        value={filters.stationId}
                        options={stationOrLocoOptions ?? []} // [{ label, value }]
                        filter
                        resetFilterOnHide
                        placeholder="Select Stations"
                        className="w-56"
                        display="chip"
                    />

                </div>
            )}
        </>
    );

    const handleEventView = (rowData: any) => {
        const params = new URLSearchParams(location.search);
        params.set("eventId", rowData.eventId);
        const currentFilters = filtersRef.current;

        if (currentFilters.tripId.length) {
            params.set("tripId", currentFilters.tripId.join(","));
        }

        if (currentFilters.date?.[0]) {
            const from = formatDateInDDMMYYYY(currentFilters.date[0]);
            params.set("fromDate", from);
        }

        if (currentFilters.date?.[1]) {
            const to = formatDateInDDMMYYYY(currentFilters.date[1]);
            params.set("toDate", to);
        } else {
            params.set("toDate", formatDateInDDMMYYYY(currentFilters.date[0]));
        }
        // localStorage.setItem(RCA_BACK_BUTTON, location.pathname + '?' + params.toString());

        navigate(`/dashboard/event-rca?${params.toString()}`);
    };

    const buildFinalPath = (path?: string, queryParams?: any, tripTime?: any) => {
        if (!path) return;

        const currentFilters = filtersRef.current;
        const params = new URLSearchParams();

        if (currentFilters.locoId.length) {
            params.set("locoId", currentFilters.locoId.join(","));
        }

        if (currentFilters.stationId.length) {
            params.set("stationId", currentFilters.stationId.join(","));
        }

        if (currentFilters.date?.[0]) {
            const from = formatDateInDDMMYYYY(currentFilters.date[0]);
            params.set("fromDate", from);
        }

        if (currentFilters.date?.[1]) {
            const to = formatDateInDDMMYYYY(currentFilters.date[1]);
            params.set("toDate", to);
        } else {
            params.set("toDate", formatDateInDDMMYYYY(currentFilters.date[0]));
        }

        if (queryParams?.tripId) {
            params.set("tripId", queryParams.tripId);
        }

        if (queryParams?.locoId) {
            params.set("locoId", queryParams.locoId);
        }
        if (path !== '/find-trips/trip-wise-summary')
            localStorage.removeItem(FIND_TRIPS_PAGINATOR_KEY)
        else
            localStorage.setItem(FIND_TRIPS_TIME, JSON.stringify(tripTime));

        navigate(
            params.toString()
                ? `${path}?${params.toString()}`
                : path
        );
    };


    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">
            <Toast ref={toast} position="top-right" />

            <div className="flex items-center justify-between w-full my-3">
                <BreadCrumb model={breadcrumb} />

                {REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW === reportType && (
                    <div className="flex flex-col items-start relative">
                        <Calendar
                            id="date"
                            dateFormat="dd/mm/yy"
                            value={filters.date}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    date: e.value as (Date | null)[],
                                })
                            }
                            selectionMode="range"
                            readOnlyInput
                            hideOnRangeSelection
                            numberOfMonths={2}
                            placeholder="Select date"
                            className="w-full min-w-64"
                            inputClassName="w-64 pr-10"
                        />

                        <IconCalendar
                            size={18}
                            stroke={1.5}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10"
                        />
                    </div>
                )}
            </div>

            {![REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW].includes(reportType) && (
                <div className="grid grid-cols-1 mb-6 pt-4">

                    <Card header={reportType}>
                        {/* Header */}
                        <div className="mb-3">
                            <h3 className="m-0 font-semibold"></h3>
                            <p className="mt-2 text-sm text-600">
                                {description}
                            </p>
                        </div>

                        {/* Filters */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Loco ID / Station ID */}
                            {/* {reportType !== 'Find Trips' && */}
                            <div>
                                <label className="block mb-2 text-sm font-medium">{isStationReport ? "Station ID" : "Loco ID"}</label>
                                <MultiSelect
                                    value={isStationReport ? filters.stationId : filters.locoId}
                                    options={stationOrLocoOptions} // [{ label, value }]
                                    onChange={(e) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            ...(isStationReport ? { stationId: e.target.value } : { locoId: e.target.value })
                                        }))
                                        setLocoOrStationValidationMsg("");
                                    }
                                    }
                                    filter
                                    resetFilterOnHide
                                    placeholder={`Select ${isStationReport ? "Station ID" : "Loco ID"}`}
                                    className="w-full"
                                    display="chip"
                                />
                                {locoOrStationvalidationMsg && (
                                    <small className="text-red-500 mt-1 block">
                                        {locoOrStationvalidationMsg}
                                    </small>
                                )}
                            </div>

                            {/* Trip ID */}
                            {tripFilterNotAllowedReportTypes && !window.location.pathname.includes('report/station-health') &&
                                <div>
                                    <label className="block mb-2 text-sm font-medium">Trip ID</label>
                                    <MultiSelect
                                        options={tripOptions} // [{ label, value }]
                                        value={filters.tripId}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                tripId: e.target.value
                                            }))
                                        }
                                        filter
                                        resetFilterOnHide
                                        placeholder="Select Trip ID"
                                        className="w-full"
                                        display="chip"
                                    />
                                    {tripIdvalidationMsg && (
                                        <small className="text-red-500 mt-1 block">
                                            {tripIdvalidationMsg}
                                        </small>
                                    )}
                                    {/* <InputText
                                    value={filters.tripId}
                                    onChange={(e) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            tripId: e.target.value
                                        }))
                                    }
                                    keyfilter={/^[0-9]*$/}
                                    placeholder="Enter Trip ID"
                                    className="w-full"
                                /> */}
                                </div>
                            }

                            {/* Date */}
                            <div className="flex flex-col items-start ">
                                <label className="block mb-2 text-sm font-medium">Date</label>
                                <div className="relative w-full">
                                    <Calendar
                                        id="date"
                                        dateFormat="dd/mm/yy"
                                        value={filters.date}
                                        onChange={(e) =>
                                            setFilters({ ...filters, date: e.value as (Date | null)[] })
                                        }
                                        selectionMode="range"
                                        readOnlyInput
                                        hideOnRangeSelection
                                        numberOfMonths={2}
                                        placeholder="Select date"
                                        className="w-full min-w-64"
                                        inputClassName="w-64 pr-10"
                                    />
                                    <IconCalendar size={18} stroke={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-4">
                            <Button
                                label="Search"
                                icon={<IconSearch size={20} />}
                                onClick={() => {
                                    clearPaginatorFromStorage();
                                    setFiltersChanged(false);
                                    setFirst(0);
                                    setPageNum(1);
                                    setRows(10);
                                    updateQueryParams();

                                    if (shouldFetchReports) {
                                        refetch();
                                    }
                                }}

                                loading={isReportDataLoading}
                                disabled={!shouldFetchReports || isReportDataLoading}
                                className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
                            />
                            <Button
                                label="Reset"
                                icon={<IconReload size={20} />}
                                outlined
                                loading={isReportDataLoading}
                                disabled={!shouldFetchReports || isReportDataLoading}
                                className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-800 dark:text-gray-200"
                                onClick={handleReset}
                            />
                        </div>
                    </Card>
                </div>
            )}

            {[REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW].includes(reportType) && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
                    <div className="md:col-span-6">
                        <CardTable config={TAG_MISSING_NEW_TABLE.LIKELY_READER_FAULTS} filters={reportsQueryParams} />
                    </div>

                    <div className="md:col-span-6">
                        <CardTable config={TAG_MISSING_NEW_TABLE.LIKELY_FAULTY_TAGS} filters={reportsQueryParams} />
                    </div>

                    {/* <div className="md:col-span-6">
                        <CardTable config={TAG_MISSING_NEW_TABLE.LOCO_X_STATION_MISS_HEATMAP} filters={reportsQueryParams} />
                    </div> */}

                    <div className="md:col-span-12">
                        <Card header={cardHeader}>
                            <BarChart id='tag-miss-catergory' data={tagMissedCategoryData} className="h-72" stacked={false} barThickness={true} />
                        </Card>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 mb-6 pt-4">
                <Card className="p-card-body-p-0" header={header}>
                    <Table
                        columns={tableColumns}
                        data={displayTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isReportDataLoading}
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        renderCell={(rowData, field, rowIndex, path) => {

                            if (reportType === "Find Trips") {
                                if (field === 'actions') {
                                    return <Tag
                                        value="View Summary"
                                        severity="success"
                                        className="cursor-pointer"
                                        onClick={() => buildFinalPath(`/find-trips/trip-wise-summary`, { tripId: rowData.tripId }, { tripStart: rowData.tripStart, tripEnd: rowData.tripEnd, locoId: rowData.locoId })}
                                    />
                                }

                                return (rowData[field] ?
                                    <a id={`row-${rowIndex}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => buildFinalPath(path, { tripId: rowData.tripId, locoId: rowData.locoId })}>
                                        {rowData[field]}
                                    </a> : rowData[field]
                                );
                            } else if (reportType === REPORT_TYPES.HEADER.LOCO_HEALTH.CHECKSUM_CHANGE_REPORT) {
                                if (['vcChangeType', 'dmi1ChangeType', 'dmi2ChangeType', 'vgw1ChangeType', 'vgw2ChangeType'].includes(field)) {
                                    return (
                                        <div className="flex gap-2">
                                            {(rowData[field] ?? []).map((row: any, index: number) => (
                                                <>
                                                    <Tag
                                                        id={`tag-${rowIndex}-${index}-${field}`}
                                                        value={shortForm(row)}
                                                        severity={getSeverity(row)}
                                                    />
                                                    <Tooltip target={`#tag-${rowIndex}-${index}-${field}`} content={row} position="top" />
                                                </>))}
                                        </div>
                                    );
                                } else if (field === 'changeSource') {
                                    return rowData[field] === 'Both'
                                        ? <Tag value={rowData[field]} severity="danger" />
                                        : rowData[field];
                                }

                            } else if ([REPORT_TYPES.HEADER.EVENTS.TAG_MISSING, REPORT_TYPES.HEADER.EVENTS.UNWANTED_BRAKING, REPORT_TYPES.HEADER.EVENTS.SIGNAL_BLANKING].includes(reportType)) {

                                if (field === 'eventId') {

                                    return (rowData[field] ?
                                        <a id={`row-${rowIndex}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => handleEventView(rowData)}>
                                            {rowData[field].substring(0, 5) + '...'}
                                        </a> : '-'
                                    );

                                } else {

                                    return rowData[field] ? (
                                        <>
                                            <Tag
                                                id={field === 'eventType' ? `tag-${rowIndex}-${field}` : undefined}
                                                value={rowData[field]}
                                                severity={getSeverity(rowData[field])}
                                            />

                                            {field === 'eventType' && (
                                                <Tooltip
                                                    target={`#tag-${rowIndex}-${field}`}
                                                    content={
                                                        rowData[field] === 'EB'
                                                            ? 'Emergency Brake'
                                                            : 'Full Service Brake'
                                                    }
                                                    position="top"
                                                />
                                            )}
                                        </>
                                    ) : (
                                        '-'
                                    );
                                }
                            } else {
                                return (rowData[field] ?
                                    <Tag
                                        value={rowData[field]}
                                        severity={getSeverity(rowData[field])}
                                    /> : '-'
                                );
                            }
                        }}

                        onPageChangeExternal={(page_num, total_entry_per_page) => {
                            setFirst((page_num - 1) * total_entry_per_page);
                            setRows(total_entry_per_page);
                            setPageNum(page_num);

                            if (reportType === "Find Trips") {


                                savePaginatorToStorage({
                                    page: page_num,
                                    rows: total_entry_per_page,
                                });
                            }
                        }}
                    />
                </Card>
            </div>
        </div>
    );
}